import { Earning } from '@/types/earnings'
import { DriverMaster } from '@/types/driverMaster'
import { Compensation } from '@/types/compensation'
import { FleetSettings, PayoutCompany } from '@/types'
import { SalaryReportRow, SalaryReportSummary } from './reportConfig'
import { formatDriverName } from '@/lib/formatters'
import { sumMoney } from '@/lib/money'
import { SalaryCalculationService } from '@/services/salaryCalculationService'
import { SalarySnapshotService } from '@/services/salarySnapshotService'
import { TaxTableService } from '@/services/taxTableService'
import { convertDriverToInfo, SalaryDriverTaxSettings } from '@/types/salarySnapshot'
import { DEFAULT_TAX_TABLE, DEFAULT_TAX_COLUMN } from '@/components/config/TaxCalculationConfig'


/**
 * Transform raw earnings and driver data into salary report rows
 * Now uses SalarySnapshotService for consistent calculations
 */
export function createSalaryReportData(
  earnings: Earning[],
  drivers: DriverMaster[],
  compensations: Compensation[] = [],
  selectedPeriod: string | null = null,
  settings: FleetSettings,
  payoutCompanies: PayoutCompany[] = [],
  payoutDate?: Date
): SalaryReportRow[] {
  const companyMap = new Map(payoutCompanies.map(c => [c.id, c.name]))
  // Get unique driver IDs from earnings and compensations
  const driverIds = [...new Set([
    ...earnings.map(e => e.driverId),
    ...compensations.map(c => c.driverId)
  ].filter(Boolean))] as string[]

  if (driverIds.length === 0) {
    return []
  }

  // Create driver lookup map
  const driverLookup = new Map(drivers.map(d => [d.id, d]))

  // Create salary report rows using SalarySnapshotService
  const reportRows: SalaryReportRow[] = []

  driverIds.forEach(driverId => {
    const driver = driverLookup.get(driverId)
    if (!driver) {
      console.warn(`Driver ${driverId} not found in drivers list`)
      return
    }

    const driverName = formatDriverName(driver.primaryName)

    // Filter earnings and compensations for this driver
    const driverEarnings = earnings.filter(e => e.driverId === driverId && !e.isDeleted)
    const driverCompensations = compensations.filter(c => c.driverId === driverId && !c.isDeleted)

    // Convert driver to info
    const driverInfo = convertDriverToInfo(driver)

    // Extract driver-specific tax settings (if any)
    const hasDriverSpecificTax = driver.taxCalculationMethod !== undefined && driver.taxCalculationMethod !== null
    let driverTaxSettings: SalaryDriverTaxSettings | undefined = undefined

    if (hasDriverSpecificTax) {
      driverTaxSettings = {
        ...(driver.taxCalculationMethod && { taxCalculationMethod: driver.taxCalculationMethod }),
        ...(driver.taxRate !== undefined && { taxRate: driver.taxRate }),
        ...(driver.taxTableName && { taxTableName: driver.taxTableName }),
        ...(driver.taxColumn !== undefined && { taxColumn: driver.taxColumn }),
        ...(driver.salaryGarnishment !== undefined && { salaryGarnishment: driver.salaryGarnishment }),
      }
    }

    // Get period from earnings or use selectedPeriod
    const periodToUse = selectedPeriod || driverEarnings[0]?.period || driverCompensations[0]?.period || ''

    // Resolve tax year from payoutDate (if provided) or fall back to period year
    const taxYear = TaxTableService.getTaxYear(periodToUse, payoutDate)

    // Create snapshot using SalarySnapshotService
    // This ensures calculations are identical to locked data
    const snapshot = SalarySnapshotService.createSnapshot(
      driverId,
      periodToUse,
      driverEarnings,
      driverCompensations,
      driverInfo,
      driverTaxSettings,
      taxYear
    )

    // Calculate values for tooltip comparison (without compensations)
    const aggregatedInputForOriginal = {
      grossEarnings: snapshot.totals.grossEarnings,
      netEarnings: snapshot.totals.netEarnings,
      tips: snapshot.totals.tips,
      cashReceived: snapshot.totals.cashReceived || 0
    }
    // Use merged settings (fleet + driver-specific) for accurate comparison
    const effectiveSettings = SalarySnapshotService.mergeSettings(
      snapshot.fleetSettings,
      driverTaxSettings
    )
    const originalCalc = SalaryCalculationService.calculateAggregatedSalary(
      aggregatedInputForOriginal,
      effectiveSettings,
      []
    )

    // Determine tax table used
    const taxCalculationMethod = driverTaxSettings?.taxCalculationMethod || settings.taxCalculationMethod || 'fixed'
    const taxTableUsed = taxCalculationMethod === 'table'
      ? (driverTaxSettings?.taxTableName?.replace(/\D/g, '') || settings.taxTableName?.replace(/\D/g, '') || DEFAULT_TAX_TABLE.replace(/\D/g, ''))
      : null
    const taxColumnUsed = taxCalculationMethod === 'table'
      ? (driverTaxSettings?.taxColumn ?? settings.taxColumn ?? DEFAULT_TAX_COLUMN)
      : null

    // Map snapshot to SalaryReportRow format
    // Note: netEarnings now comes from totals (includes compensation adjustments)
    reportRows.push({
      driverId,
      driverName,
      personalCode: driver.personalCode,
      email: driver.email,
      payoutCompany: driver.payoutCompanyId ? companyMap.get(driver.payoutCompanyId) : undefined,
      grossEarnings: snapshot.totals.grossEarnings,
      netEarnings: snapshot.totals.netEarnings + snapshot.totals.netEarningsCompensations - snapshot.totals.netEarningsDeductions,
      tips: snapshot.totals.tips,
      cashReceived: snapshot.totals.cashReceived,
      vatAmount: snapshot.totals.vatAmount,
      shareableAmount: snapshot.totals.shareableAmount,
      afterFleetCommission: snapshot.totals.afterFleetCommission,
      vacationPay: snapshot.totals.vacationPay,
      grossSalary: snapshot.totals.grossSalary,
      netEarningsCompensations: snapshot.totals.netEarningsCompensations,
      netEarningsDeductions: snapshot.totals.netEarningsDeductions,
      grossSalaryCompensations: snapshot.totals.grossSalaryCompensations,
      grossSalaryDeductions: snapshot.totals.grossSalaryDeductions,
      finalGrossSalary: snapshot.totals.finalGrossSalary,
      employerTaxAmount: snapshot.totals.employerTaxAmount,
      totalEmployerCost: snapshot.totals.totalEmployerCost,
      netSalary: snapshot.totals.netSalary,
      netSalaryCompensations: snapshot.totals.netSalaryCompensations,
      netSalaryDeductions: snapshot.totals.netSalaryDeductions,
      finalNetSalary: snapshot.totals.finalNetSalary,
      // Compensation net values (for display/export)
      netEarningsCompensationNet: snapshot.totals.netEarningsCompensations - snapshot.totals.netEarningsDeductions,
      grossSalaryCompensationNet: snapshot.totals.grossSalaryCompensations - snapshot.totals.grossSalaryDeductions,
      netSalaryCompensationNet: snapshot.totals.netSalaryCompensations - snapshot.totals.netSalaryDeductions,
      // Tips handling fields
      untaxedTips: snapshot.totals.untaxedTips,
      amountToPay: snapshot.totals.amountToPay,
      // Original values before compensations for tooltip "before" comparison
      originalNetEarnings: snapshot.totals.netEarnings,
      originalGrossSalary: originalCalc.grossSalary,
      // Compensation details for tooltip functionality
      compensations: driverCompensations,
      hasCompensations: driverCompensations.some(comp => comp.type === 'compensation'),
      hasDeductions: driverCompensations.some(comp => comp.type === 'deduction'),
      // Tax information
      taxAmount: snapshot.totals.taxAmount,
      effectiveTaxRate: snapshot.totals.effectiveTaxRate,
      taxTableUsed,
      taxColumnUsed,
      taxCalculationMethod,
      isDriverSpecificTax: hasDriverSpecificTax
    })
  })

  return reportRows
}

/**
 * Calculate summary statistics from salary report data
 */
export function calculateSalarySummary(data: SalaryReportRow[]): SalaryReportSummary {
  return {
    totalDrivers: data.length,
    totalGrossEarnings: sumMoney(data.map(row => row.grossEarnings)),
    totalNetEarnings: sumMoney(data.map(row => row.netEarnings)),
    totalTips: sumMoney(data.map(row => row.tips)),
    totalVatAmount: sumMoney(data.map(row => row.vatAmount)),
    totalGrossSalary: sumMoney(data.map(row => row.finalGrossSalary)),
    totalEmployerTaxAmount: sumMoney(data.map(row => row.employerTaxAmount)),
    totalEmployerCost: sumMoney(data.map(row => row.totalEmployerCost)),
    totalNetSalary: sumMoney(data.map(row => row.netSalary)),
    totalFinalNetSalary: sumMoney(data.map(row => row.finalNetSalary)),
    totalUntaxedTips: sumMoney(data.map(row => row.untaxedTips || 0)),
    totalAmountToPay: sumMoney(data.map(row => row.amountToPay)),
    totalCashReceived: sumMoney(data.map(row => row.cashReceived || 0))
  }
}

/**
 * Filter salary report data based on search term
 */
export function filterSalaryData(data: SalaryReportRow[], searchTerm: string): SalaryReportRow[] {
  if (!searchTerm) return data

  const term = searchTerm.toLowerCase()
  return data.filter(row =>
    row.driverName.toLowerCase().includes(term) ||
    row.driverId.toLowerCase().includes(term)
  )
}

/**
 * Sort salary report data
 */
export function sortSalaryData(
  data: SalaryReportRow[],
  column: keyof SalaryReportRow,
  direction: 'asc' | 'desc'
): SalaryReportRow[] {
  return [...data].sort((a, b) => {
    const aValue = a[column]
    const bValue = b[column]

    if (aValue === null || aValue === undefined) return 1
    if (bValue === null || bValue === undefined) return -1

    let comparison = 0
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue)
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      comparison = aValue - bValue
    } else {
      comparison = String(aValue).localeCompare(String(bValue))
    }

    return direction === 'asc' ? comparison : -comparison
  })
}